import logger from './Logger';

const COMPONENT = 'SentenceBoundaryUtils';

/**
 * Handles the sentence boundary enhancement response from the Reprocess API.
 *
 * When the API returns `prev_div_corrected` and `prev_div_id`, it means a new
 * division has started (sequence_id === 0) and the backend has re-evaluated the
 * previous division's corrected text with cross-boundary context.
 *
 * This function overwrites the UI paragraph for `prev_div_id` with the
 * corrected text supplied in `prev_div_corrected`.
 *
 * @param {Object} params
 * @param {Object}   params.reprocessApiResponse  - Full JSON response from the /reprocess endpoint
 * @param {Function} params.setTranscriptSegments - State setter for live transcript segments
 * @param {Function} params.setPersistentSegments - State setter for persistent/historical segments
 * @param {Function} params.sendSegmentToViewers  - Function to broadcast the correction to viewers
 * @param {Object}   params.viewerDeps            - Deps required by sendSegmentToViewers
 * @param {string}   [params.highlightColor]      - Highlight colour for changed words (default: blue)
 * @returns {boolean} true if an overwrite was applied, false otherwise
 */
export function applyPrevDivCorrection({
  reprocessApiResponse,
  setTranscriptSegments,
  setPersistentSegments,
  sendSegmentToViewers,
  viewerDeps,
  highlightColor = '#0066cc',
}) {
  // Guard: only act when both fields are present
  const prevDivCorrected =
    typeof reprocessApiResponse?.prev_div_corrected === 'string'
      ? reprocessApiResponse.prev_div_corrected.trim()
      : null;

  const prevDivId =
    typeof reprocessApiResponse?.prev_div_id === 'string'
      ? reprocessApiResponse.prev_div_id.trim()
      : null;

  if (!prevDivCorrected || !prevDivId) {
    // Nothing to do — this is normal for same-division processing
    return false;
  }

  logger.info('Applying prev_div_corrected sentence boundary enhancement', {
    component: COMPONENT,
    action: 'APPLY_PREV_DIV_CORRECTION',
    context: {
      prevDivId,
      prevDivCorrectedLength: prevDivCorrected.length,
    },
  });


  /**
   * Build the highlighted HTML for the overwritten paragraph.
   *
   * We obtain the original text from the first matching segment so the
   * word-diff highlight is accurate. If no original is found we fall back
   * to rendering the corrected text without diff highlighting.
   */
  const buildHtml = () => {
    return prevDivCorrected;
  };

  /**
   * Updater applied to both transcriptSegments and persistentSegments.
   * Finds ALL segments belonging to prevDivId and replaces their text with
   * the cross-boundary corrected version.
   *
   * Strategy: only the LAST segment in the div carries the full corrected
   * text (which represents the complete paragraph); earlier partial segments
   * within the same div are left visually as-is but flagged as corrected so
   * we don't double-apply.  For simplicity — and because the UI renders
   * segments grouped by div — we overwrite every segment in that div with
   * the new text on the first pass and hide subsequent ones.
   *
   * Simpler approach used here: replace ONLY the first segment of that div
   * with the full corrected paragraph text, and remove / blank the remaining
   * segments of the same div so the paragraph renders cleanly.
   */
  const updater = (segments) => {
    if (!Array.isArray(segments)) return segments;

    const html = buildHtml(segments);

    let firstSegmentReplaced = false;
    let changed = false;

    const next = segments.map((seg) => {
      if (!seg) return seg;

      const matchesDivId = seg.div_id === prevDivId || seg.divid === prevDivId;
      if (!matchesDivId) return seg;

      // Replace the first segment with the full corrected text
      if (!firstSegmentReplaced) {
        firstSegmentReplaced = true;
        changed = true;
        return {
          ...seg,
          text: html,
          corrected: true,
          prevDivCorrected: true, // flag so we can identify this correction
          originalText: seg.originalText || seg.text,
        };
      }

      // Blank subsequent segments in the same div so only one paragraph shows
      changed = true;
      return {
        ...seg,
        text: '',
        corrected: true,
        prevDivCorrected: true,
        originalText: seg.originalText || seg.text,
      };
    });

    return changed ? next : segments;
  };

  setTranscriptSegments((prev) => updater(prev));
  setPersistentSegments((prev) => updater(prev));

  // Broadcast the corrected paragraph to viewers if the function is provided
  if (sendSegmentToViewers && viewerDeps) {
    try {
      // Use sequence_id 0 because this is the start of a new division context
      sendSegmentToViewers(
        prevDivCorrected,
        false,      // isPartial
        'segment',  // actionType
        prevDivId,
        0,          // sequence_id
        new Date().toISOString().slice(0, 19),
        viewerDeps,
        true,       // is_corrected
      );

      logger.info('Sent prev_div_corrected text to viewers', {
        component: COMPONENT,
        action: 'PREV_DIV_SENT_TO_VIEWERS',
        context: { prevDivId },
      });
    } catch (viewerErr) {
      logger.warn('Failed to send prev_div_corrected to viewers', {
        component: COMPONENT,
        action: 'PREV_DIV_VIEWER_SEND_FAILED',
        context: { prevDivId },
        error: viewerErr,
      });
    }
  }

  logger.info('prev_div_corrected sentence boundary enhancement applied', {
    component: COMPONENT,
    action: 'PREV_DIV_CORRECTION_SUCCESS',
    context: { prevDivId },
  });
  return true;
}